import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { docsApi, mattersApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, MagnifyingGlass, Upload, TrashSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

export default function Documents() {
    const [docs, setDocs] = useState([]);
    const [matters, setMatters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [q, setQ] = useState("");
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [docList, mList] = await Promise.all([docsApi.list(), mattersApi.list()]);
            setDocs(docList);
            setMatters(mList);
        } catch {
            toast.error("Failed to load documents");
        }
        setLoading(false);
    };
    useEffect(() => {
        load();
    }, []);

    const upload = async (file) => {
        setUploading(true);
        try {
            const d = await docsApi.upload(file);
            toast.success("Uploaded and processed");
            navigate(`/documents/${d.id}`);
        } catch (e) {
            console.error(e);
            toast.error("Upload failed");
        }
        setUploading(false);
    };

    const remove = async (id) => {
        if (!window.confirm("Remove this document?")) return;
        await docsApi.remove(id);
        await load();
    };

    const linkToMatter = async (docId, matterId) => {
        try {
            const updated = await docsApi.update(docId, { matter_id: matterId || null });
            setDocs((arr) => arr.map((d) => (d.id === docId ? updated : d)));
            const m = matters.find((x) => x.id === matterId);
            toast.success(matterId ? `Linked to "${m?.title || "matter"}"` : "Unlinked from matter");
        } catch (e) {
            console.error(e);
            const msg = e?.response?.data?.detail || "Failed to update document";
            toast.error(msg);
        }
    };

    const filtered = docs.filter(
        (d) => !q.trim() || d.original_filename?.toLowerCase().includes(q.toLowerCase()) || d.doc_type?.toLowerCase().includes(q.toLowerCase()),
    );

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="documents-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Library</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Documents</h1>
                    <HelpButton topic="documents" testid="help-documents" />
                </div>
                </div>
                <label className="inline-flex items-center gap-2 border border-border rounded-sm bg-primary text-primary-foreground px-4 py-2 cursor-pointer text-sm hover:opacity-90" data-testid="upload-doc-btn">
                    <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.docx,.png,.jpg,.jpeg,.webp,.txt"
                        onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                    />
                    <Upload size={15} />
                    {uploading ? "Processing…" : "Upload PDF / DOCX / Image"}
                </label>
            </div>

            <div className="relative mb-4 max-w-md">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Filter by filename or type…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="doc-search" />
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading…</div>
            ) : !filtered.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-center">
                    <FileText size={28} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
                    <div className="font-serif text-xl mb-1">No documents yet</div>
                    <div className="text-sm text-muted-foreground">Upload PDFs, DOCX files, or scanned images.</div>
                </div>
            ) : (
                <div className="border border-border rounded-md overflow-hidden bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-3 font-semibold">Name</th>
                                <th className="text-left p-3 font-semibold">Linked matter</th>
                                <th className="text-left p-3 font-semibold hidden md:table-cell">Type</th>
                                <th className="text-left p-3 font-semibold hidden lg:table-cell">Language</th>
                                <th className="text-left p-3 font-semibold hidden lg:table-cell">Pages</th>
                                <th className="text-left p-3 font-semibold hidden lg:table-cell">OCR</th>
                                <th className="text-right p-3 font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((d) => (
                                <tr key={d.id} className="border-t border-border hover:bg-muted/40" data-testid={`doc-row-${d.id}`}>
                                    <td className="p-3">
                                        <Link to={`/documents/${d.id}`} className="font-medium hover:underline">
                                            {d.original_filename}
                                        </Link>
                                        {d.processing_status === "queued" && <span className="ml-2 chip chip-ai">queued</span>}
                                        {d.processing_status === "processing" && <span className="ml-2 chip chip-ai">processing</span>}
                                        {d.processing_status === "error" && <span className="ml-2 chip chip-doc text-destructive">error</span>}
                                    </td>
                                    <td className="p-3 min-w-[200px]">
                                        <Select
                                            value={d.matter_id || "none"}
                                            onValueChange={(val) => linkToMatter(d.id, val === "none" ? null : val)}
                                        >
                                            <SelectTrigger
                                                className="h-8 text-xs"
                                                data-testid={`doc-matter-select-${d.id}`}
                                            >
                                                <SelectValue placeholder="(unlinked)" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">— Unlinked —</SelectItem>
                                                {matters.map((m) => (
                                                    <SelectItem key={m.id} value={m.id}>
                                                        {m.title}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </td>
                                    <td className="p-3 hidden md:table-cell"><span className="chip chip-doc">{d.doc_type || "document"}</span></td>
                                    <td className="p-3 hidden lg:table-cell">{d.language}</td>
                                    <td className="p-3 hidden lg:table-cell">{d.num_pages}</td>
                                    <td className="p-3 hidden lg:table-cell">{d.used_ocr ? <span className="chip chip-ai">OCR</span> : "—"}</td>
                                    <td className="p-3 text-right">
                                        <button
                                            className="text-muted-foreground hover:text-destructive"
                                            onClick={() => remove(d.id)}
                                            data-testid={`doc-delete-${d.id}`}
                                            aria-label="Delete document"
                                        >
                                            <TrashSimple size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {!loading && !!filtered.length && !matters.length && (
                <div className="mt-3 text-xs text-muted-foreground border border-dashed border-border rounded-sm p-3 bg-muted/30" data-testid="docs-no-matters-hint">
                    Tip: <Link to="/matters" className="underline">create a matter</Link> first, then link your documents to it from the dropdown above. Linked documents auto-attach when you generate drafts for that matter.
                </div>
            )}
        </div>
    );
}
